import { v4 as uuidv4 } from 'uuid';
import { MemriseError } from 'src/utils/errors';
import { isLocalProfilePreview } from 'src/utils/env/isLocalProfilePreview';
import { randomInteger } from 'src/utils/numbers';
import { isRomanizationLabel } from 'src/api/settings/learningToggles';
import {
  AudioMultipleChoiceTestCard,
  Difficulty,
  Learnable,
  LearnableAudioValue,
  LearnableRowInfo,
  LearnableValue,
  LearnableValueKind,
  Mem,
  SampleSentence,
  MultipleChoiceTestCard,
  Presentation,
  PresentationCard,
  Prompt,
  ResponseModel,
  Screen,
  TappingTestCard,
  TemplateName,
  Test,
  TestCard,
  TestPromptType,
  TypingTestCard,
  VideoActor,
} from 'src/api/learningSessions/types';
import { Prompt as TestLearnableCardPrompt } from './TestLearnableCard';
import { TestPrompt } from './TestLearnableCard/Prompt';
import { SupportedTemplateName, TestResponse } from './TestLearnableCard/Response';
import { isTraceable } from './TestLearnableCard/Response/TracingResponse/utils';

interface BaseCardData {
  kind: 'Presentation' | 'Test';
}

export interface PresentationCardData extends BaseCardData {
  kind: 'Presentation';
  item: {
    label: string;
    value: string;
    alternatives: string[];
    kind: LearnableValueKind;
    showBigger?: boolean;
  };
  definition: {
    label: string;
    value: string;
    kind: LearnableValueKind;
    showBigger?: boolean;
  };
  attributeValues: string[];
  audioSrcs: string[];
  videoSrc: string | null;
  /** Actor IDs for the selected `videoSrc`; null when there is no video. */
  videoActorIds: number[] | null;
  /** The actor shown as the face of the selected `videoSrc`, when one has a face video. */
  videoActorFace?: VideoActorFace | null;
  extraInfo: LearnableRowInfo[];
  previousIncorrectAnswer: string | null;
  /** Fun fact about the word being learned */
  nugget?: string;
  mems?: Mem[];
  sampleSentences?: SampleSentence[];
  pitchAccent?: number[] | null;
  kana?: string | null;
  /** Native script form when the taught item is romanization (or another reading). */
  nativeScript?: string | null;
  romanization?: string | null;
  phoneticSpelling?: string | null;
}

export interface TestCardData extends BaseCardData {
  kind: 'Test';
  prompt: TestPrompt;
  response: TestResponse;
  attributeValues: string[];
  postAnswerInfo?: LearnableRowInfo;
  testId: string;
  timeCardShown: number;
}

export type CardData = TestCardData | PresentationCardData;

export const getScreenTemplate = <S extends Screen>(
  screenType: TemplateName,
  { screens }: Learnable,
): S => {
  const template = Object.values(screens).find(screen => screen.template === screenType.toString());
  if (!template) {
    throw new MemriseError(`Could not find the screen for this learnable type: ${screenType}`);
  }
  return template as S;
};

export const getPresentationScreenTemplate = (learnable: Learnable) =>
  getScreenTemplate<Presentation>(TemplateName.PRESENTATION, learnable);

export const getSingleValueNullable = (learnableValue: LearnableValue): string | null => {
  const { value, kind } = learnableValue;

  // From the API, Kind = text will always be a single value
  if (kind === LearnableValueKind.TEXT) {
    return value as string;
  }

  const randomPosition = randomInteger({ max: value.length });

  // From the API, Video will always be flat list of strings
  // From the API, Image is always a list of strings
  if (kind === LearnableValueKind.VIDEO || kind === LearnableValueKind.IMAGE) {
    const chosenValue = value[randomPosition];
    return chosenValue as string;
  }

  // From the API, remaining are kind audio; a potentially empty list of dictionaries
  // each of which have two keys "normal" and "slow"
  if (value && value.length > 0) {
    const chosenAudioObject = value[randomPosition] as LearnableAudioValue;
    return chosenAudioObject.normal as string;
  }
  return null;
};

export const getSingleValue = (learnableValue: LearnableValue): string => {
  const singleValue = getSingleValueNullable(learnableValue);
  if (singleValue === null) {
    throw new Error(`Could not get single value`);
  }
  return singleValue;
};

/** Actor ID for Emma; her videos are excluded from selection (low popularity). */
export const EMMA_VIDEO_ACTOR_ID = 582;

export interface VideoActorFace {
  id: number;
  name: string | null;
  facePictureSrc: string;
}

const placeholderFacePictureSrc = (name: string | null) =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#FFBB00"/><text x="32" y="42" font-family="sans-serif" font-size="28" font-weight="700" text-anchor="middle" fill="#1A1A1A">${(name?.[0] ?? '?').toUpperCase()}</text></svg>`,
  )}`;

const getActorFace = (actors: VideoActor[]): VideoActorFace | null => {
  const actor = actors.find(({ face_picture_url }) => !!face_picture_url);
  if (actor?.face_picture_url) {
    return { id: actor.id, name: actor.name, facePictureSrc: actor.face_picture_url };
  }
  const [previewActor] = actors;
  return previewActor && isLocalProfilePreview()
    ? {
        id: previewActor.id,
        name: previewActor.name,
        facePictureSrc: placeholderFacePictureSrc(previewActor.name),
      }
    : null;
};

export interface SelectedVideo {
  src: string | null;
  actorIds: number[];
  actorFace: VideoActorFace | null;
}

/** Every video in a video learnable value that can be shown, skipping any featuring Emma. */
export const getSelectableVideos = (video: LearnableValue): SelectedVideo[] => {
  const urls = video.value as string[];
  return urls
    .map((src, index) => ({ src, actors: video.actors?.[index] ?? [] }))
    .filter(({ actors }) => !actors.some(actor => actor.id === EMMA_VIDEO_ACTOR_ID))
    .map(({ src, actors }) => ({
      src,
      actorIds: actors.map(actor => actor.id),
      actorFace: getActorFace(actors),
    }));
};

/**
 * Picks one video URL (and its actor IDs) from a video learnable value.
 * Videos featuring Emma are never selected. When every candidate features her,
 * returns `src: null` so the UI shows no video rather than an Emma clip.
 */
export const selectVideo = (video: LearnableValue): SelectedVideo => {
  if (video.kind !== LearnableValueKind.VIDEO) {
    return { src: getSingleValue(video), actorIds: [], actorFace: null };
  }

  const videos = getSelectableVideos(video);
  if (videos.length === 0) {
    return { src: null, actorIds: [], actorFace: null };
  }

  return videos[randomInteger({ max: videos.length })];
};

export const selectVideoSrc = (video: LearnableValue): string | null => selectVideo(video).src;

export const getValues = (learnableValue: LearnableValue): string[] => {
  const { value, kind } = learnableValue;

  // From the API, Kind = text will always be a single value
  if (kind === LearnableValueKind.TEXT) {
    return [value] as string[];
  }

  // From the API, Video will always be flat list of strings
  // From the API, Image is always a list of strings
  if (kind === LearnableValueKind.VIDEO || kind === LearnableValueKind.IMAGE) {
    return value as string[];
  }

  // From the API, remaining are kind audio; a list of dictionaries each of which have two keys
  // "normal" and "slow"
  const audioValues = value as LearnableAudioValue[];

  return audioValues.map(i => i.normal);
};

export const getTestScreenTemplate = (templateName: TemplateName, learnable: Learnable) =>
  getScreenTemplate<Test>(templateName, learnable);

export const getNumDistractors = (difficulty: Difficulty | null) => {
  if (difficulty === 'hard') return 5;

  return 3;
};

export const getTypingChoices = (testScreen: Test) => {
  // correct can be a string[][] for tapping tests, but is always string[] for typing tests
  const answers: string[] = testScreen.correct as string[];
  const keyboardChoices = testScreen.choices;

  const direction = testScreen.answer.direction;
  return { answers, keyboardChoices, direction };
};

const checkLearnableValue = (learnableValue: LearnableValue | null): LearnableValue => {
  /* istanbul ignore next: internal consistency check */
  if (!learnableValue) {
    throw new Error('Missing learnable value');
  }
  return learnableValue;
};

/**
 * Takes a prompt object from a screen and returns a subset of
 * the props required to display the prompt to the user
 */
export const getPrompt = (prompt: Prompt, promptType: TestPromptType): TestLearnableCardPrompt => {
  let learnableValue: LearnableValue;
  switch (promptType) {
    case 'Text':
      learnableValue = checkLearnableValue(prompt.text);
      return {
        promptType,
        text: getSingleValue(learnableValue),
        direction: learnableValue.direction,
        showBigger: shouldShowBigger(learnableValue.style),
        label: learnableValue.label,
      };
    case 'Image':
      learnableValue = checkLearnableValue(prompt.image);
      return {
        promptType,
        image: getSingleValue(learnableValue),
        direction: learnableValue.direction,
      };
    case 'Video': {
      learnableValue = checkLearnableValue(prompt.video);
      const { src, actorIds } = selectVideo(learnableValue);
      /* istanbul ignore next: learnables with only Emma videos have no prompt media */
      if (!src) {
        throw new Error('Could not get single value');
      }
      return {
        promptType,
        video: src,
        direction: learnableValue.direction,
        ...(actorIds.length > 0 && { actorIds }),
      };
    }
    case 'Audio':
      learnableValue = checkLearnableValue(prompt.audio);
      return {
        promptType,
        audio: getSingleValue(learnableValue),
        direction: learnableValue.direction,
      };

    /* istanbul ignore next: internal consistency check */
    default:
      throw new Error(`promptType of "${promptType}" not known`);
  }
};

export const getTemplateName = (responseModel: ResponseModel): SupportedTemplateName => {
  switch (responseModel) {
    case 'MultipleChoice':
      return TemplateName.MULTIPLE_CHOICE;
    case 'ReverseMultipleChoice':
      return TemplateName.REVERSED_MULTIPLE_CHOICE;
    /* istanbul ignore next: to be removed in WEBSITE-2541 */
    case 'AudioMultipleChoice':
      return TemplateName.AUDIO_MULTIPLE_CHOICE;
    case 'Typing':
      return TemplateName.TYPING;
    case 'Tapping':
      return TemplateName.TAPPING;
    default:
      // When a new test type is suggested by memlearning, if web client
      // doesnt yet support it, default to multiple choice
      return TemplateName.MULTIPLE_CHOICE;
  }
};

/*
 * The API sends an array 'style' which contains the item
 * 'bigger' if the LearnableValue should be shown bigger
 */
export const shouldShowBigger = (style: string[]): boolean => {
  return style.indexOf('bigger') >= 0;
};

export const getTestCardData = (
  learnable: Learnable,
  testCard: TestCard,
  timeCardShown: number,
  options?: { tracingEnabled?: boolean; disableCopy?: boolean },
): TestCardData => {
  const testId = uuidv4();
  const promptType = testCard.testType.prompt;
  const responseModel = testCard.testType.model;

  let templateName = getTemplateName(responseModel);

  const shouldSubstituteWithTracing =
    options?.tracingEnabled &&
    templateName === TemplateName.TYPING &&
    isTraceable(learnable.learningElement);

  if (shouldSubstituteWithTracing) {
    templateName = TemplateName.TRACING;
  }

  const screen = getTestScreenTemplate(
    shouldSubstituteWithTracing ? TemplateName.TYPING : templateName,
    learnable,
  );

  /* istanbul ignore next: prompt should only be null when gap_prompt is set (grammar only) */
  if (screen.prompt === null) {
    throw new MemriseError("Item doesn't have a prompt", 404);
  }

  const prompt = getPrompt(screen.prompt, promptType);
  const attributeValues = screen.attributes.map(attribute => attribute.value);
  let postAnswerInfo;
  if (screen.postAnswerInfo) {
    const { label, kind } = screen.postAnswerInfo;
    postAnswerInfo = {
      label,
      value: getSingleValue(screen.postAnswerInfo),
      kind,
    };
  }

  const commonAttributes = {
    kind: 'Test' as const,
    prompt,
    attributeValues,
    postAnswerInfo,
    testId,
    timeCardShown,
  };

  switch (templateName) {
    case TemplateName.TYPING: {
      const { answers, keyboardChoices, direction } = getTypingChoices(screen);
      const showBigger = shouldShowBigger(screen.answer.style);

      const typingTestCard: TappingTestCard = testCard as TypingTestCard;
      const { isCopy } = typingTestCard;
      return {
        response: {
          templateName,
          direction,
          answers,
          keyboardChoices,
          // Difficult-words copy tests reveal the answer as greyed-out input
          // text; disableCopy strips that scaffolding when the learner has
          // chosen to hide the answer before/during the test.
          isCopy: options?.disableCopy ? false : isCopy,
          showBigger,
          label: screen.answer.label,
        },
        ...commonAttributes,
      };
    }
    case TemplateName.TAPPING: {
      const tappingTestCard: TappingTestCard = testCard as TappingTestCard;
      const { answers, choices, isCopy } = tappingTestCard;
      const showBigger = shouldShowBigger(screen.answer.style);
      const direction = screen.answer.direction;

      return {
        response: {
          templateName,
          direction,
          answers,
          choices,
          isCopy: options?.disableCopy ? false : isCopy,
          showBigger,
        },
        ...commonAttributes,
      };
    }
    case TemplateName.AUDIO_MULTIPLE_CHOICE: {
      const { answer, choices } = testCard as AudioMultipleChoiceTestCard;
      return {
        response: {
          templateName,
          direction: 'target',
          correctChoice: answer.stringValue,
          choices: choices.map(({ stringValue }) => stringValue),
        },
        ...commonAttributes,
      };
    }
    case TemplateName.REVERSED_MULTIPLE_CHOICE:
    case TemplateName.MULTIPLE_CHOICE: {
      const { choices, answer, responseType } = testCard as MultipleChoiceTestCard;
      const direction = screen.answer.direction;

      const showBigger = shouldShowBigger(screen.answer.style);

      return {
        response: {
          templateName,
          direction,
          choices,
          correctChoice: answer,
          responseType,
          showBigger,
        },
        ...commonAttributes,
      };
    }
    case TemplateName.TRACING: {
      const { answers } = getTypingChoices(screen);
      const direction = screen.answer.direction;
      return {
        response: {
          templateName,
          direction,
          character: learnable.learningElement,
          answers,
        },
        ...commonAttributes,
      };
    }
  }
};

export const getAudio = (learnable: Learnable): string | null => {
  const { audio } = getPresentationScreenTemplate(learnable);
  return audio !== null ? getSingleValueNullable(audio) : null;
};

/**
 * Gets most of the props needed for presentation learnable card
 */
export const LEARNABLE_ROMANIZATION_LABEL = 'Romanization';

export const withLearnableRomanization = (
  extraInfo: LearnableRowInfo[],
  romanization: string | null | undefined,
  itemValue: string,
): LearnableRowInfo[] => {
  if (!romanization || romanization === itemValue) {
    return extraInfo;
  }

  const alreadyPresent = extraInfo.some(
    info => isRomanizationLabel(info.label) || info.value === romanization,
  );
  if (alreadyPresent) {
    return extraInfo;
  }

  return [
    {
      label: LEARNABLE_ROMANIZATION_LABEL,
      value: romanization,
      kind: LearnableValueKind.TEXT,
    },
    ...extraInfo,
  ];
};

export const getPresentationCardData = (
  learnable: Learnable,
  card?: PresentationCard,
): PresentationCardData => {
  const currentScreenTemplate = getPresentationScreenTemplate(learnable);

  const { item, definition, visibleInfo, hiddenInfo, attributes, audio, video, nugget, mems } =
    currentScreenTemplate;

  const attributeValues = attributes.map(attribute => attribute.value);
  const audioSrcs = (audio && getValues(audio)) || [];
  const selectedVideo = video ? selectVideo(video) : null;
  const videoSrc = selectedVideo?.src ?? null;
  const videoActorIds = selectedVideo?.actorIds ?? null;
  const videoActorFace = selectedVideo?.actorFace ?? null;
  const itemValue = item && getSingleValue(item);
  const definitionValue = definition && getSingleValue(definition);

  const extraInfo: LearnableRowInfo[] = visibleInfo.concat(hiddenInfo).map(info => ({
    value:
      getSingleValueNullable(info) ||
      /* istanbul ignore next: untested branch of code, please test */ 'undefined',
    kind: info.kind,
    label: info.label,
  }));

  return {
    item: {
      label: item.label,
      value: itemValue,
      alternatives: item.alternatives,
      kind: item.kind,
      showBigger: shouldShowBigger(item.style),
    },
    definition: {
      label: definition.label,
      value: definitionValue,
      kind: definition.kind,
      showBigger: shouldShowBigger(definition.style),
    },
    attributeValues,
    audioSrcs,
    videoSrc,
    videoActorIds,
    videoActorFace,
    extraInfo: withLearnableRomanization(extraInfo, learnable.romanization, itemValue),
    kind: 'Presentation',
    previousIncorrectAnswer: card?.previousIncorrectAnswer ?? null,
    nugget,
    mems,
    sampleSentences: learnable.sampleSentences,
    pitchAccent: learnable.pitchAccent,
    kana: learnable.kana,
    nativeScript: learnable.nativeScript,
    romanization: learnable.romanization,
    phoneticSpelling: learnable.phoneticSpelling,
  };
};
